import { ArrowDown, X } from "lucide-react";
import { FC, useEffect, useRef } from "react";

import { cn } from "../../lib/utils";

interface LogsPanelProps {
    logs: string[];
    isVisible: boolean;
    onClose: () => void;
    className?: string;
    agentVisible?: boolean;
}

const LogsPanel: FC<LogsPanelProps> = ({
    logs,
    isVisible,
    onClose,
    className,
    agentVisible = false,
}) => {
    const logContainerRef = useRef<HTMLDivElement>(null);

    // Auto-scroll to bottom when new logs are added
    useEffect(() => {
        if (logContainerRef.current) {
            logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
        }
    }, [logs]);

    const scrollToBottom = () => {
        if (logContainerRef.current) {
            logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
        }
    };

    if (!isVisible) return null;

    return (
        <div
            className={cn(
                "w-full h-[300px] border-t border-gray-200 transition-transform duration-200 ease-in-out bg-white",
                agentVisible ? "" : "border-r",
                className
            )}
            style={{
                maxWidth: agentVisible ? 'calc(100% - 400px)' : '100%'
            }}
        >
            <div className="logspanel-header">
                <div className="flex items-center space-x-2 py-1 px-2 w-full">
                    <div className="text-xs font-medium text-gray-600">Logs</div>
                    <div className="flex-1"></div>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={scrollToBottom}
                            className="logspanel-scroll-button"
                            title="Scroll to bottom"
                        >
                            <ArrowDown className="w-3 h-3" />
                        </button>
                        {onClose && (
                            <button
                                onClick={onClose}
                                className="logspanel-close-button"
                                title="Close logs"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div
                className="logspanel-content"
                ref={logContainerRef}
            >
                <div className="logspanel-log-list">
                    {logs.length > 0 ? (
                        <div className="logspanel-entries">
                            {logs.map((log, index) => (
                                <div
                                    key={index}
                                    className="logspanel-log-line text-xs"
                                >
                                    <span className="logspanel-line-number">{index + 1}</span>
                                    <span className="logspanel-prompt">~</span>
                                    <span className="logspanel-log-text">{log}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="logspanel-no-logs text-xs">No logs yet...</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LogsPanel;